import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Card } from '#pie/components/card/Card';
import { Divider } from '#pie/components/divider/Divider';
import { FailureInfo } from '#pie/components/failure-info/FailureInfo';
import { Page, PageHeader } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { TicketTableEan } from '#pie/components/ticket-table-ean/TicketTableEan';
import { productIcons } from '#pie/constants/productIcons';
import { useGetConnectionInformation } from '#pie/internal/api/pie-bff';
import type { StepProps } from './TicketCreateNonConformity';
import { useNonConformityFormContext } from './TicketCreateNonConformity';

const schema = z.object({});

type TicketsForm = z.infer<typeof schema>;

export const TicketCreateNonConformityTickets = ({
  wizardActions,
  onSubmit,
  onBack,
  stepper,
}: StepProps) => {
  const { t } = useTranslation();
  const { t: transtic } = useTranslation('ticket');
  const {
    data: { electricityEan },
  } = useNonConformityFormContext();
  const { data: elecData } = useGetConnectionInformation(
    { ean18: electricityEan! },
    { query: { enabled: !!electricityEan } },
  );

  const hasElecFailure = !!elecData?.failureEventCode;

  const formMethods = useForm<TicketsForm>({ resolver: zodResolver(schema) });
  const { handleSubmit } = formMethods;

  return (
    <Page>
      <PageHeader
        title={transtic('create.non_conformity.page_title')}
        previousText={t('common.steps.previous')}
        backOnClick={onBack}
        details={stepper}
      />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card className="mb-6 max-w-4xl p-6">
            <Stack gap="lg">
              <Stack gap="sm">
                <Text as="h2" variant="h5" className="text-content-heading">
                  {transtic('create.prior_tickets.failures.title')}
                </Text>
                {hasElecFailure ? (
                  <Stack gap="sm">
                    {hasElecFailure && electricityEan && (
                      <FailureInfo ean18={electricityEan} />
                    )}
                  </Stack>
                ) : (
                  transtic(
                    'create.prior_tickets.failures.no_failures_for_ean',
                    { eans: electricityEan },
                  )
                )}
              </Stack>
              {electricityEan && (
                <Stack gap="sm">
                  <Stack direction="row" gap="sm" className="items-center">
                    <Icon
                      className="text-content-heading shrink-0"
                      icon={productIcons.Electricity}
                    />
                    <Text as="h2" variant="h5" className="text-content-heading">
                      {transtic('create.prior_tickets.earlier_tickets', {
                        ean: electricityEan,
                      })}
                    </Text>
                  </Stack>
                  <TicketTableEan ean={electricityEan} />
                </Stack>
              )}
              <Divider />
              <Stack direction="row" className="justify-between" gap="lg">
                {wizardActions}
              </Stack>
            </Stack>
          </Card>
        </form>
      </FormProvider>
    </Page>
  );
};
